import Menu from '@/global/components/Menu'
import { TextField } from '@/global/components/TextField'
import { Button as ButtonUI } from '@/global/components/ui/button'
import { cn } from '@/lib/utils'
import {
    BookmarkPlusIcon,
    CheckIcon,
    ChevronDownIcon,
    PencilIcon,
    Trash2Icon,
} from 'lucide-react'
import React from 'react'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import useFilterPresets from '../hooks/useFilterPresets'
import FilterModelPro, {
    GridFilterItemPro,
    GridFilterModelPro,
} from '../utils/filterModelPro'
import { GridColDefPro } from '../utils/types'

/**
 * O destaque padrão do menu preenche a linha com `primary`, que aqui brigaria
 * com os botões coloridos de cada preset — o realce fica só no fundo.
 */
const ITEM = 'h-10 focus:bg-accent focus:text-foreground'

/** Botão de ação dentro da linha do preset. */
function AcaoPreset({
    className,
    ...props
}: React.ComponentProps<typeof ButtonUI>) {
    return (
        <ButtonUI
            type="button"
            variant="ghost"
            size="icon"
            className={cn('size-8 shrink-0 rounded-full', className)}
            {...props}
        />
    )
}

export default function FilterPreset({
    onFilterModelChange,
    filterModel,
    columns,
}: {
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    filterModel: GridFilterModelPro
    columns: GridColDefPro[]
}) {
    const {
        filtros,
        adicionarPreset,
        atualizarPreset,
        excluirPreset,
        isLoading,
    } = useFilterPresets({ columns })

    /** `id` do preset em edição — enquanto houver um, o menu não fecha ao clicar. */
    const [editando, setEditando] = React.useState<number | null>(null)
    const [nome, setNome] = React.useState('')

    function carregarPreset(items: GridFilterItemPro[]) {
        onFilterModelChange(filtroVazio)
        // O modelo precisa ser esvaziado antes de receber o do preset, senão as
        // linhas antigas do painel se misturam com as novas.
        setTimeout(() => {
            onFilterModelChange({
                ...filtroVazio,
                ...new FilterModelPro({ items }).filterModel,
            })
        }, 100)
    }

    function salvarNome(id: number) {
        atualizarPreset(id, nome)
        setEditando(null)
    }

    return (
        <Menu.Root
            className="w-full"
            onOpenChange={aberto => !aberto && setEditando(null)}
        >
            <Menu.Trigger>
                <ButtonUI
                    type="button"
                    variant="outline"
                    disabled={isLoading}
                    className="h-[30px] w-full justify-between px-3 text-[0.8rem] font-normal text-muted-foreground"
                >
                    Filtros salvos
                    <ChevronDownIcon className="size-4 opacity-50" />
                </ButtonUI>
            </Menu.Trigger>
            <Menu.List className="max-h-[200px] w-[250px] overflow-y-auto">
                {!filterModel.items.length && !filtros.length && (
                    <Menu.Item disabled>
                        <em>Nenhum filtro salvo</em>
                    </Menu.Item>
                )}
                {!!filterModel.items.length && (
                    <Menu.Item
                        className={ITEM}
                        onSelect={() => adicionarPreset(filterModel.items)}
                    >
                        <BookmarkPlusIcon className="mr-1" />
                        Salvar predefinição atual
                    </Menu.Item>
                )}
                {filtros.map(filtro => {
                    const emEdicao = editando === filtro.id

                    return (
                        <Menu.Item
                            key={filtro.id}
                            className={cn(ITEM, 'gap-1')}
                            // Em edição o clique é do campo de nome, não do item.
                            onSelect={evento => {
                                if (emEdicao) return evento.preventDefault()
                                carregarPreset(filtro.items)
                            }}
                            // O Radix move o foco para o item ao passar o mouse
                            // — tiraria o cursor de dentro do campo de nome.
                            onPointerMove={evento => {
                                if (emEdicao) evento.preventDefault()
                            }}
                        >
                            {emEdicao ? (
                                <>
                                    <TextField
                                        variant="outlined"
                                        autoFocus
                                        size="small"
                                        unHelperText
                                        value={nome}
                                        maxLength={40}
                                        className="flex-1"
                                        inputClassName="h-8 text-[0.85rem]"
                                        onChange={e => setNome(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter')
                                                salvarNome(filtro.id)
                                            else e.stopPropagation()
                                        }}
                                    />
                                    <AcaoPreset
                                        title="Salvar"
                                        onClick={() => salvarNome(filtro.id)}
                                    >
                                        <CheckIcon className="text-success" />
                                    </AcaoPreset>
                                </>
                            ) : (
                                <>
                                    <span
                                        title={`Carregar ${filtro.nome}`}
                                        className="relative max-w-[160px] flex-1 overflow-hidden"
                                    >
                                        {filtro.nome}
                                    </span>
                                    <AcaoPreset
                                        title="Alterar nome"
                                        onClick={e => {
                                            e.stopPropagation()
                                            setNome(filtro.nome)
                                            setEditando(filtro.id)
                                        }}
                                    >
                                        <PencilIcon className="text-success" />
                                    </AcaoPreset>
                                </>
                            )}
                            <AcaoPreset
                                title="Excluir"
                                onClick={e => {
                                    e.stopPropagation()
                                    excluirPreset(filtro.id)
                                }}
                            >
                                <Trash2Icon className="text-error" />
                            </AcaoPreset>
                        </Menu.Item>
                    )
                })}
            </Menu.List>
        </Menu.Root>
    )
}
